/**
 * Événements (PRD §8) : scriptés (`trigger.year` = année du mandat 1–5) ou conditionnels (`trigger.condition`
 * évaluée sur l'état de fin d'année, puis `probability` tirée avec le générateur seedé). Chaque événement se
 * déclenche au plus une fois par mandat. L'option retenue est `choices[event.id]` ; à défaut l'option de référence
 * (`reference_option_id`, sinon la moins coûteuse — ADR 0009), ce que le journal signale. Le coût politique DÉCLARÉ de l'option
 * (hors argent) est payé immédiatement ; coûts, coût politique lié aux montants (règle symétrique) et canaux de l'option
 * s'appliquent à partir de l'année suivante (profils d'événement : tableau fini, scalaire = une seule année).
 */
import { test as testExpr } from "./expr.js";
import { hypTarget } from "./profile.js";
import type { Scope } from "./expr.js";
import { param } from "./params.js";
import { referenceOption } from "./politics.js";
import type { Rng } from "./rng.js";
import { PCT } from "./state.js";
import type { EngineState } from "./state.js";
import type { Choices, GameEvent, Measure, ParamSet, ScheduledEvent } from "./types.js";
import type { YearResult } from "./year.js";

const clamp = (x: number, a: number, b: number): number => Math.max(a, Math.min(b, x));

/** Variables exposées aux conditions d'événements (liste blanche : tout autre identifiant est une erreur de contenu). */
export function buildScope(s: EngineState, r: YearResult["report"], catalogue: Measure[], params?: ParamSet): Scope {
  const scope: Record<string, number> = {
    year: s.turn, // année du mandat (1–5) qui vient d'être simulée
    deficit_pct: r.deficit_pct,
    debt_pct: s.debt_ratio,
    growth: r.growth,
    potential_growth: s.gpot,
    unemployment: s.unemployment,
    poverty: s.poverty,
    inflation: s.inflation,
    confidence: s.confidence,
    cohesion: s.cohesion,
    eu: s.eu,
    capital: s.capital,
    labour: s.labour,
    trade: s.trade,
    interest: r.interest,
    spread: r.spread,
    apparent_rate: s.apparent_rate,
    below_three_years: s.below_three,
    lost_dividend: s.lost_dividend,
    debt_cancelled_bn: s.debt_cancelled,
    rabot: r.rabot_pt,
    rabot_years: s.rabot_years,
    service_avg: r.svc_avg,
    debt_financed: r.debt_financed,
    measures_cost: r.measures_cost,
    majority: s.majority,
    purchasing_power: s.purchasing_power,
  };
  for (const [k, v] of Object.entries(r.indicators)) scope[k] = v;
  for (const [mid, v] of Object.entries(s.service_index)) scope[`service_index.${mid}`] = v;
  // majority_lost : 1 si la majorité est sous `majority_loss_threshold` (ADR 0005) ; sans params, 0.
  scope["majority_lost"] = params && s.majority < param(params, "majority_loss_threshold") ? 1 : 0;
  for (const m of catalogue) {
    const adopted = s.adopted[m.id];
    scope[`measure_active.${m.id}`] = adopted === undefined ? 0 : 1;
    scope[`measure_years.${m.id}`] = adopted === undefined ? 0 : s.turn - adopted;
  }
  return scope;
}

export interface FiredEvent {
  event: GameEvent;
  option_id: string;
  /** Vrai si aucune option n'était fournie dans `choices` (option de référence appliquée par défaut). */
  defaulted: boolean;
  /** Issue du risque imposée (séquence figée de l'attribution) ; absente → tirée avec le générateur. */
  risk_hit?: boolean;
}

/** Détecte les événements de fin d'année, dans l'ordre du catalogue ; consomme le rng pour les probabilités. */
export function detectEvents(s: EngineState, scope: Scope, events: GameEvent[], choices: Choices, rng: Rng): FiredEvent[] {
  const out: FiredEvent[] = [];
  for (const e of events) {
    if (s.fired.includes(e.id)) continue;
    const t = e.trigger;
    if (t.year !== undefined && t.year !== s.turn) continue;
    if (t.condition !== undefined && !testExpr(t.condition, scope)) continue;
    if (t.year === undefined && t.condition === undefined) continue;
    if (t.probability !== undefined && t.probability < 1 && rng.next() >= t.probability) continue;
    if (e.options.length === 0) throw new Error(`événement « ${e.id} » sans option`);
    const chosen = choices[e.id];
    const opt = chosen === undefined ? undefined : e.options.find((o) => o.id === chosen);
    if (chosen !== undefined && !opt) throw new Error(`choix : option « ${chosen} » inconnue pour l'événement « ${e.id} »`);
    out.push({ event: e, option_id: (opt ?? referenceOption(e)).id, defaulted: opt === undefined });
  }
  return out;
}

/** Événements imposés pour l'année `turn` (séquence figée, ADR 0009 §4) : conditions et probabilités ignorées, aucun tirage. */
export function scheduledEvents(turn: number, schedule: ScheduledEvent[], eventsById: Record<string, GameEvent>): FiredEvent[] {
  const out: FiredEvent[] = [];
  for (const x of schedule) {
    if (x.turn !== turn) continue;
    const e = eventsById[x.event_id];
    if (!e) throw new Error(`séquence d'événements : événement inconnu « ${x.event_id} »`);
    if (!e.options.some((o) => o.id === x.option_id)) throw new Error(`séquence d'événements : option « ${x.option_id} » inconnue pour « ${x.event_id} »`);
    out.push({ event: e, option_id: x.option_id, defaulted: false, risk_hit: x.risk_hit });
  }
  return out;
}

/**
 * Applique les événements déclenchés : coût politique immédiat, issue défavorable éventuelle (`option.risk`, tirée avec `rng`),
 * retour sur une annulation de dette (`option.undo_debt_cancellation`, immédiat), effets différés à l'année suivante, journal.
 * Mute `s` et `year`. `rng` est requis dès qu'une option retenue porte un `risk`.
 */
export function applyEvents(s: EngineState, fired: FiredEvent[], params: ParamSet, year: YearResult["year"], rng?: Rng, catalogue: Measure[] = []): void {
  const applied: Record<string, string> = {};
  const cap = (x: number): number => clamp(x, param(params, "capital_min"), param(params, "capital_max"));
  for (const f of fired) {
    const opt = f.event.options.find((o) => o.id === f.option_id);
    if (!opt) throw new Error(`option « ${f.option_id} » introuvable`);
    s.fired.push(f.event.id);
    let riskHit = false;
    if (opt.risk) {
      if (f.risk_hit !== undefined) riskHit = f.risk_hit;
      else {
        if (!rng) throw new Error(`événement « ${f.event.id} » : option à risque sans générateur`);
        riskHit = rng.next() < opt.risk.probability;
      }
    }
    (year.risks_hit ??= {})[f.event.id] = riskHit;
    s.active_events.push({ event_id: f.event.id, option_id: opt.id, since: s.turn, ...(riskHit ? { risk_hit: true } : {}) });
    s.capital = cap(s.capital - opt.political_cost - (riskHit ? (opt.risk?.political_cost ?? 0) : 0));
    applied[f.event.id] = opt.id;
    year.events_fired.push(f.event.id);
    year.journal.push(`Événement : ${f.event.title} → ${opt.label}${f.defaulted ? " (aucun choix transmis : option de référence appliquée par défaut)" : ""}.`);
    if (opt.risk) year.journal.push(riskHit ? `Le pari échoue : ${opt.risk.label}.` : `Le risque (${opt.risk.label}) ne s'est pas réalisé.`);
    if (opt.undo_debt_cancellation && s.debt_cancelled > 0) {
      s.debt += s.debt_cancelled;
      for (const m of catalogue) {
        if (s.adopted[m.id] !== undefined && (hypTarget(m) === "debt_share" || m.fx.debt_share !== undefined) && !s.retired.includes(m.id)) s.retired.push(m.id);
      }
      year.journal.push(
        `La dette annulée (${Math.round(s.debt_cancelled)} Md€) est réintégrée au stock ; le dividende Banque de France est rétabli ; la mesure d'annulation est retirée du programme.`,
      );
      s.lost_dividend = 0;
      s.debt_cancelled = 0;
      s.debt_ratio = (s.debt / s.gdp) * PCT;
    }
  }
  if (fired.length > 0) {
    year.choices_applied = applied;
    year.kpis["political_capital"] = s.capital;
    year.kpis["debt_bn"] = s.debt;
    year.kpis["debt_pct_gdp"] = s.debt_ratio;
  }
}
